/* @flow */

import React, { useContext, useState } from "react";
import { Link } from "react-router-dom";
import { Route, Switch, useHistory, useLocation } from "react-router";
import { usePreserveScrollPosition } from "helpers/react-router-on-navigation";
import Header from "app/Header";
import Routes from "routes";
import { Helmet } from "react-helmet-async";
import { useTranslate } from "@awardit/react-use-translate";
import { StoreInfoContext } from "entrypoint/shared";
import { UiProvider, useUi, MODE } from "helpers/ui";
import { NotificationBar, AppFooter, Button, SkipToContent } from "@crossroads/ui-components";
import PriceLock from "components/PriceLock";
import CookieConsent from "components/CookieConsent";
import useCookie from "helpers/hooks/use-cookie";
import useShowPrices from "helpers/use-show-prices";
import { CmsPageData, CmsBlockData } from "state/data";
import { CouponCode } from "@crossroads/shop-views";

import styles from "./styles.scss";

const App = (): React$Node => {
  const history = useHistory();
  const location = useLocation();
  const t = useTranslate();
  const onCheckout = (location ? location.pathname : "").includes("checkout");
  const { info, categories } = useContext(StoreInfoContext);
  const cookieOpts = { domain: `.${typeof window !== "undefined" ? window.location.host.replace(/:\d+$/, "") : ""}`, expires: 365 };
  const [consent, setConsent, consentRead] = useCookie("cookie-consent", "initial", cookieOpts);
  const [hideNotificationBar, setHideNotificationBar] = useState(false);
  const [toggleCookieDialogue, setToggleCookieDialogue] = useState(false);

  /* Hook checks for price-lock url query and unlocks prices if the
  *  secret is correct. Exists in app to ensure it runs throughout all pages.
  */ useShowPrices(true);

  const footerTerms = () => (
    <div className={styles.termsField}>
      <div>
        <Link className={styles.link} to="/villkor">
          {t("FOOTER.TERMS")}
        </Link>
        &nbsp;|&nbsp;
        <Link className={styles.link} to="/privacy-policy">
          {t("FOOTER.PRIVACY_POLICY")}
        </Link>
        &nbsp;|&nbsp;
        <Button
          aria-expanded={toggleCookieDialogue ?? "true"}
          aria-controls="cookieDialogue"
          className={styles.link}
          onClick={() => setToggleCookieDialogue(true)}
        >
          {t("FOOTER.COOKIES")}
        </Button>
        &nbsp;|&nbsp;
        <Link className={styles.link} to="/accessibility">
          {t("FOOTER.ACCESSIBILITY")}
        </Link>
      </div>
      <p dangerouslySetInnerHTML={{ __html: t("FOOTER.COPYRIGHT", { year: new Date().getFullYear() }) }} />
    </div>
  );

  usePreserveScrollPosition(history);

  return (
    <UiProvider>
      <CmsPageData.Provider url="cookie-consent">
        <CmsBlockData.Provider identifier="mini-banner">
          <Helmet
            titleTemplate={`${info.titlePrefix || ""} %s ${info.titleSuffix || ""}`}
            link={[
              { rel: "apple-touch-icon", sizes: "57x57", href: "/assets/browser/apple-touch-icon.png" },
              { rel: "apple-touch-icon", sizes: "60x60", href: "/assets/browser/apple-touch-icon.png" },
              { rel: "apple-touch-icon", sizes: "72x72", href: "/assets/browser/apple-touch-icon.png" },
              { rel: "apple-touch-icon", sizes: "76x76", href: "/assets/browser/apple-touch-icon.png" },
              { rel: "apple-touch-icon", sizes: "114x114", href: "/assets/browser/apple-touch-icon.png" },
              { rel: "apple-touch-icon", sizes: "120x120", href: "/assets/browser/apple-touch-icon.png" },
              { rel: "apple-touch-icon", sizes: "144x144", href: "/assets/browser/apple-touch-icon.png" },
              { rel: "apple-touch-icon", sizes: "152x152", href: "/assets/browser/apple-touch-icon.png" },
              { rel: "apple-touch-icon", sizes: "180x180", href: "/assets/browser/apple-touch-icon.png" },
              { rel: "icon", type: "image/png", size: "16x16", href: "/assets/browser/favicon-16x16.png" },
              { rel: "icon", type: "image/png", size: "32x32", href: "/assets/browser/favicon-32x32.png" },
              { rel: "manifest", href: "/manifest.json" },
            ]}
            meta={[
              { name: "msapplication-TileColor", content: "#ffffff" },
              { name: "theme-color", content: "#ffffff" },
            ]}
            htmlAttributes={{ lang: info.locale.slice(0, 2) }}
          />
          <Switch>
            <Route
              render={() => (
                <div className={styles.app}>
                  <SkipToContent />

                  {consentRead &&
                    <div className={styles.notices}>
                      <CookieConsent
                        country={info.defaultCountry}
                        isVisible={consentRead && (consent === "initial" || toggleCookieDialogue)}
                        consent={consent}
                        setConsent={setConsent}
                        consentRead={consentRead}
                        setToggleCookieDialogue={setToggleCookieDialogue}
                      />
                    </div>
                  }

                  <Header />

                  <main id="main" className={styles.mainContent}>
                    <Routes />
                  </main>

                  <PriceLock />
                  <CouponCode />

                  {!onCheckout &&
                    <AppFooter
                      className={styles.footer}
                      activeLinks={["start", "categories", "brands", "search"]}
                      categories={categories}
                      contentTerms={footerTerms}
                      MODE={MODE}
                      useUi={useUi}
                    />
                  }

                  {info.popUp && info.popUp.body && !hideNotificationBar &&
                    <NotificationBar
                      className={styles.notificationBar}
                      text={info.popUp.body}
                      closeBtnTitle={t("NOTIFICATIONBAR.CLOSE")}
                      onClose={() => setHideNotificationBar(true)}
                    />
                  }
                </div>
              )}
            />
          </Switch>
        </CmsBlockData.Provider>
      </CmsPageData.Provider>
    </UiProvider>
  );
};

export default App;
